import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as BottomRightCornerIcon } from "assets/svgs/navigation/map/bottomrightcorner.svg";

import CloseButton from "components/layout/CloseButton";
import ARCControlMapView from "components/navigation/map/ARCControlMapView";
import StreamView from "components/streaming/StreamView";
import ButtonsAndViewsGroup from "components/layout/ButtonsAndViewsGroup";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CenterIcon } from "assets/svgs/navigation/map/center.svg";
import { getOsmTileUrlTemplate } from "lib/consts/osmTiles";
import { useARC } from "providers/ARC/ARCContext";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import OsmMap from "components/map/OsmMap";

/** Match prior `w-[16.75rem]` × `h-50` using root font size. */
const defaultSizePx = () => {
    const px =
        typeof window !== "undefined"
            ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
            : 16;
    return {
        w: Math.round(16.75 * px),
        h: Math.round(12.5 * px),
    };
};

const MIN_W = 200;
const MIN_H = 160;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const OSM_MINI_VIEW_DEFAULT_ZOOM = 16;

const MiniView = ({
    type = "map",
    visible = false,
    onClose = () => {},
    latitude = 37.7749,
    longitude = -122.4194,
    hasFix = false,
}) => {
    const { t } = useTranslation("control");
    const { selectedARC } = useARC();
    const arcId = useFleetArcId();
    const isSecure = useIsHttpSecure();
    const [size, setSize] = useState(defaultSizePx);
    const [osmRecenterVersion, setOsmRecenterVersion] = useState(0);
    const [followGps, setFollowGps] = useState(false);
    const [tilesUnavailable, setTilesUnavailable] = useState(false);
    const tileFailCountRef = useRef(0);

    const osmTileUrl = useMemo(
        () => getOsmTileUrlTemplate(selectedARC?.host, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const tileEventHandlers = useMemo(
        () => ({
            tileload: () => {
                tileFailCountRef.current = 0;
            },
            tileerror: () => {
                tileFailCountRef.current += 1;
                if (tileFailCountRef.current >= 3) {
                    setTilesUnavailable(true);
                }
            },
        }),
        []
    );

    useEffect(() => {
        if (type === "osm" && visible) {
            tileFailCountRef.current = 0;
            setTilesUnavailable(false);
        }
    }, [type, visible, osmTileUrl]);

    const openStreetMapCenter = useMemo(() => {
        const lat = clamp(Number.isFinite(latitude) ? latitude : 37.7749, -85, 85);
        const lon = clamp(Number.isFinite(longitude) ? longitude : -122.4194, -180, 180);
        return { lat, lon };
    }, [latitude, longitude]);

    const onResizePointerDown = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const el = e.currentTarget;
            el.setPointerCapture(e.pointerId);
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = size.w;
            const startH = size.h;

            const onMove = (ev) => {
                const maxW = Math.round(window.innerWidth * 0.5);
                const maxH = Math.round(window.innerHeight * 0.5);
                const dw = ev.clientX - startX;
                const dh = ev.clientY - startY;
                setSize({
                    w: clamp(startW + dw, MIN_W, maxW),
                    h: clamp(startH + dh, MIN_H, maxH),
                });
            };

            const onUp = () => {
                try {
                    el.releasePointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onUp);
            };

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
        },
        [size.w, size.h]
    );

    if (!visible) {
        return null;
    }

    return (
        <div
            className="relative isolate overflow-visible"
            style={{ width: size.w, height: size.h }}
        >
            <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden rounded-r5 bg-black100">
                <div className="relative z-0 min-h-0 min-w-0 flex-1 overflow-hidden">
                    <ButtonsAndViewsGroup className="bottom-2 left-2" />
                    {type === "map" ? (
                        <ARCControlMapView isMiniView />
                    ) : type === "osm" ? (
                        <div className="relative h-full w-full">
                            <div
                                className="absolute"
                                style={{
                                    top: "-8px",
                                    left: "-8px",
                                    width: "calc(100% + 16px)",
                                    height: "calc(100% + 16px)",
                                }}
                            >
                                <OsmMap
                                    locations={[
                                        {
                                            id: "current-gps",
                                            latitude: openStreetMapCenter.lat,
                                            longitude: openStreetMapCenter.lon,
                                        },
                                    ]}
                                    tileUrl={osmTileUrl}
                                    zoom={OSM_MINI_VIEW_DEFAULT_ZOOM}
                                    interactive
                                    followLocationId={followGps ? "current-gps" : undefined}
                                    recenterKey={osmRecenterVersion}
                                    resizeKey={`${size.w}x${size.h}`}
                                    tileEventHandlers={tileEventHandlers}
                                    className="h-full w-full"
                                />
                            </div>
                            <FunctionalButton
                                icon={<CenterIcon className="h-5 w-5" />}
                                className={`absolute right-2 top-2 z-[1001] flex h-9 w-9 items-center justify-center rounded-full border shadow-md ${
                                    followGps
                                        ? "border-primary2 bg-primary2 text-black"
                                        : "border-black20 bg-white text-black80 hover:bg-black10"
                                }`}
                                hoverText={followGps ? "GPS Follow: On" : "GPS Follow: Off"}
                                onClick={() => {
                                    setFollowGps((prev) => {
                                        const next = !prev;
                                        if (next) {
                                            setOsmRecenterVersion((version) => version + 1);
                                        }
                                        return next;
                                    });
                                }}
                            />
                            <div
                                className={`pointer-events-none absolute left-1 top-1 z-[1001] rounded px-1.5 py-0.5 text-h5 font-semibold ${
                                    hasFix
                                        ? "bg-primary2/90 text-black"
                                        : "bg-red-950/80 text-red-300"
                                }`}
                            >
                                {hasFix ? t("controlPage.gpsFixed") : t("controlPage.gpsNoFix")}
                            </div>
                            {tilesUnavailable && (
                                <div className="pointer-events-none absolute inset-x-2 bottom-8 z-[1001] rounded bg-red-950/85 px-2 py-1 text-center text-h5 text-red-200">
                                    {t("controlPage.osmTilesUnavailable")}
                                </div>
                            )}
                            <div className="pointer-events-none absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white/80">
                                © OpenStreetMap contributors
                            </div>
                        </div>
                    ) : (
                        <StreamView className="h-full w-full" touchFocusEnabled={false} />
                    )}
                </div>
                <CloseButton
                    className="pointer-events-auto absolute right-2 top-2 z-[10010] text-black60 hover:text-black40"
                    onClick={onClose}
                />
            </div>
            <button
                type="button"
                aria-label="Resize mini view"
                className="pointer-events-auto absolute bottom-0 right-0 z-[10010] flex h-10 w-10 cursor-nwse-resize touch-none translate-x-1.5 translate-y-1.5 items-center justify-center p-0 text-black60 hover:text-primary2"
                onPointerDown={onResizePointerDown}
            >
                <BottomRightCornerIcon
                    className="pointer-events-none h-10 w-10 shrink-0 [&_path]:fill-current"
                    aria-hidden
                />
            </button>
        </div>
    );
};

export default MiniView;
